import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Award, Calendar, ArrowRight } from 'lucide-react';
import { companyInfo, teamMembers as staticTeamMembers, achievements } from '../data/siteData';
import { Button } from '../components/ui/button';
import { contentApi } from '../services/api';

export default function AboutPage() {
  const [aboutData, setAboutData] = useState({
    heading: "Building Trust Since 1998",
    subheading: "Modern Arch (Modern Group Nagpur) has been at the forefront of transforming Nagpur's real estate landscape for over two decades.",
    story_title: "Our Story",
    story_paragraph_1: "Modern Group is a leading Nagpur-based real estate conglomerate operating through its premier sister concerns, Modern Arch Developers and Modern Builder. With a core philosophy that every structure must be \"Beautiful on the Surface, Strong at the Core,\" the group specializes in crafting 100% Vastu-compliant residential spaces that blend aesthetic elegance with earthquake-resistant engineering.",
    story_paragraph_2: "From the luxurious 3BHK layouts of Modern Height II to the lifestyle-centric 2 & 3BHK apartments at Modern Height, the group is dedicated to providing Nagpur families with \"Space to Grow, Room to Breathe.\"",
    team: staticTeamMembers
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.getSection('about_page')
      .then(res => {
        if (res && res.content) {
          setAboutData(res.content);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
            About Us
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit mt-4">
            {aboutData.heading}
          </h1>
          <p className="text-lg text-slate-600 font-manrope mt-4 max-w-2xl">
            {aboutData.subheading}
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/7581120/pexels-photo-7581120.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Modern Arch Team"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mb-6">
                {aboutData.story_title}
              </h2>
              <p className="text-slate-600 font-manrope leading-relaxed mb-6">
                {aboutData.story_paragraph_1}
              </p>
              <p className="text-slate-600 font-manrope leading-relaxed mb-8">
                {aboutData.story_paragraph_2}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-[#F0F8FF] rounded-xl">
                  <span className="text-3xl font-light text-[#47ace2] font-outfit">
                    {companyInfo.projectsCompleted}
                  </span>
                  <p className="text-slate-600 text-sm font-manrope mt-1">Projects Delivered</p>
                </div>
                <div className="p-4 bg-[#F0F8FF] rounded-xl">
                  <span className="text-3xl font-light text-[#47ace2] font-outfit">
                    {companyInfo.happyFamilies}
                  </span>
                  <p className="text-slate-600 text-sm font-manrope mt-1">Happy Families</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-8 border border-slate-700 rounded-2xl">
              <div className="w-14 h-14 bg-[#47ace2]/20 rounded-full flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#47ace2]" />
              </div>
              <h3 className="text-2xl font-semibold font-outfit mb-4">Our Vision</h3>
              <p className="text-slate-300 font-manrope leading-relaxed">
                To be the Nagpurs most trusted constructions partner, recognized for creating landmark that are
                beautiful on the surface and strong at the core. While setting the gold standard for
                structural safety and earthquake-re engineering in every home we build.
              </p>
            </div>
            <div className="p-8 border border-slate-700 rounded-2xl">
              <div className="w-14 h-14 bg-[#47ace2]/20 rounded-full flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#47ace2]" />
              </div>
              <h3 className="text-2xl font-semibold font-outfit mb-4">Our Mission</h3>
              <p className="text-slate-300 font-manrope leading-relaxed">
                To build earthquake-resistant, modern, and luxurious homes that provide quality living,
                peace of mind, and Vastu-compliant designs for every family. Through advanced amenities, transparent practices, and RERA-registered developments, we are committed to delivering trust, safety, and lasting value in every project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              Leadership
            </span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mt-4">
              Meet Our Team
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {(aboutData.team || staticTeamMembers).map((member, index) => (
              <div
                key={index}
                data-testid={`team-member-${index}`}
                className="text-center group"
              >
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 font-outfit">
                  {member.name}
                </h3>
                <p className="text-[#47ace2] font-manrope mb-3">{member.position}</p>
                <p className="text-slate-600 text-sm font-manrope">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mt-4">
              Key Milestones
            </h2>
          </div>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 hidden md:block" />

            <div className="space-y-12">
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  data-testid={`milestone-${index}`}
                  className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`inline-block p-6 bg-white rounded-2xl shadow-sm ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                      }`}>
                      <span className="text-[#47ace2] font-semibold font-outfit text-lg">
                        {achievement.year}
                      </span>
                      <h3 className="text-xl font-semibold text-slate-900 font-outfit mt-2">
                        {achievement.title}
                      </h3>
                      <p className="text-slate-600 font-manrope mt-2">{achievement.desc}</p>
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-[#47ace2] rounded-full flex items-center justify-center z-10 flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              Recognition
            </span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mt-4">
              Awards & Certifications
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Best Builder Award', org: 'Nagpur Builders Association', year: '2023' },
              { title: 'Green Building Certified', org: 'IGBC', year: '2023' },
              { title: 'Excellence in Construction', org: 'CREDAI Maharashtra', year: '2022' },
            ].map((award, index) => (
              <div
                key={index}
                className="p-8 bg-[#F0F8FF] rounded-2xl text-center hover:shadow-lg transition-shadow"
              >
                <Award className="w-12 h-12 text-[#47ace2] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 font-outfit">
                  {award.title}
                </h3>
                <p className="text-slate-600 font-manrope mt-2">{award.org}</p>
                <p className="text-[#47ace2] font-semibold mt-1">{award.year}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#47ace2] to-[#1f7db3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight font-outfit mb-6">
            Let's Build Your Dream Together
          </h2>
          <p className="text-white/90 font-manrope mb-8">
            Ready to find your perfect home? Get in touch with our team today.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#47ace2] hover:bg-slate-100 rounded-full px-8 py-6 text-lg">
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
